import { createFileRoute } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import { useState } from 'react'
import { SlideShow } from '@/routes/_landing/-components/slideshow'
import { Pricing } from '@/routes/_landing/-components/pricing'
import { ContactDialog } from '@/routes/_landing/-components/contact-dialog'
import { ProfilesCount } from '@/routes/_landing/-components/profiles-count'
import { ThemedVideo } from '@/routes/_landing/-components/themed-video'
import { assetUrl } from '@/lib/asset-url'
import { trpc } from '@/main'
import { Background } from '@/routes/_landing/-components/background'
import {
  IconDiscoverYourProfile,
  IconCompareResults,
  IconAnalyzeYourTeams,
  IconAnalyzeMarket,
} from '@/components/custom/icons'
import { Feature } from './-components/feature'

export const Route = createFileRoute('/_landing/professionals')({
  loader: ({ context }) => {
    context.queryClient.prefetchQuery(trpc.common.getProfilesCount.queryOptions())
  },
  component: RouteComponent,
  staticData: { landingHeader: { aboveBackground: true } },
})

function RouteComponent() {
  const { t } = useLingui()
  const [openContact, setOpenContact] = useState(false)

  return (
    <>
      <Background
        style={{
          background: `
            radial-gradient(at 20% 6%, var(--color-blue) 0, transparent 100%),
            radial-gradient(at 81% 11%, hsl(242, 100%, 70%) 0, transparent 75%)
          `,
        }}
      />

      {/* Hero Section */}
      <section className="mb-15 flex flex-col items-center">
        <div className="max-w-225 p-8 text-center">
          <h1 className="mb-8 text-[32px] leading-tight font-bold whitespace-pre-wrap text-white md:text-[42px]">
            {t`The easiest and fastest way\nto evaluate your talent`}
          </h1>
          <p className="mb-0 text-[18px] leading-normal font-medium whitespace-pre-wrap text-white">
            {t`Discover your Psychographic Profile and find out how you fit into\nyour positions, in your teams and in the profiles the market demands.`}
          </p>
          <a
            href="/register/applicant"
            className="mt-7.5 mb-2.5 inline-block rounded-[5px] bg-white px-3 py-3 text-[18px] font-bold text-[#333] transition-shadow hover:shadow-[0px_0px_15px_-5px_white]"
          >
            {t`Get started for free`}
          </a>
          <div className="my-6.25">
            <ProfilesCount className="text-[18px] font-bold text-white" />
            <span className="ml-2 text-[18px] font-medium text-white">{t`profiles analyzed`}</span>
          </div>
        </div>

        <div className="px-5">
          <ThemedVideo
            light={assetUrl('videos/landing.mp4')}
            dark={assetUrl('videos/landing-dark.mp4')}
            posterLight={assetUrl('videos/landing.png')}
            posterDark={assetUrl('videos/landing-dark.png')}
            className="h-165 w-225 rounded-[10px] bg-gray-100 shadow-2xl max-md:h-auto max-md:w-full dark:bg-gray-800"
          />
        </div>
      </section>

      <SlideShow />

      {/* Features */}
      <Feature
        title={t`Discover your profile`}
        Icon={IconDiscoverYourProfile}
        description={t`Fill out our tests and discover your Psychographic Profile. It's easy, it's reliable, and it will add value to your career.`}
        video={assetUrl('videos/test.mp4')}
        videoDark={assetUrl('videos/test-dark.mp4')}
        image={assetUrl('videos/test.png')}
        imageDark={assetUrl('videos/test-dark.png')}
        width={482 / 1.2}
        height={542 / 1.2}
        link={{
          text: t`Start the test`,
          url: '/register/applicant',
        }}
      />

      <Feature
        title={t`Compare your results`}
        Icon={IconCompareResults}
        description={t`Add your preferred positions or accept invitations from companies and recruiters. You will discover your level of fit with each position and see the details of your match.`}
        video={assetUrl('videos/compare.mp4')}
        videoDark={assetUrl('videos/compare-dark.mp4')}
        image={assetUrl('videos/compare.png')}
        imageDark={assetUrl('videos/compare-dark.png')}
        width={900 / 1.5}
        height={700 / 1.5}
        link={{
          text: t`Start comparing`,
          url: '/register/applicant',
        }}
      />

      <Feature
        title={t`Analyze your teams`}
        Icon={IconAnalyzeYourTeams}
        description={t`Compare the profile of your teams with yours and with that of each of its members. Identify matches and optimize team performance.`}
        video={assetUrl('videos/teams.mp4')}
        videoDark={assetUrl('videos/teams-dark.mp4')}
        image={assetUrl('videos/teams.png')}
        imageDark={assetUrl('videos/teams-dark.png')}
        width={800 / 1.5}
        height={800 / 1.5}
        link={{
          text: t`Create your first team`,
          url: '/register/applicant',
        }}
      />

      <Feature
        title={t`Explore the market`}
        Icon={IconAnalyzeMarket}
        description={t`Discover the standard profile of the professionals who perform the positions you are interested in. Analyze the global market interactively.`}
        video={assetUrl('videos/market.mp4')}
        videoDark={assetUrl('videos/market-dark.mp4')}
        image={assetUrl('videos/market.png')}
        imageDark={assetUrl('videos/market-dark.png')}
        width={900 / 1.5}
        height={800 / 1.5}
        link={{
          text: t`Access the market`,
          url: '/register/applicant',
        }}
      />

      <Pricing setOpenContact={setOpenContact} userType="applicant" />

      {/* Contact CTA */}
      <h2 className="mb-0 text-center text-[20px] font-semibold text-gray-800 dark:text-gray-200">
        {t`Any questions?`}
      </h2>
      <button
        type="button"
        onClick={() => setOpenContact(true)}
        className="text-primary mx-auto mt-0 mb-50 flex cursor-pointer items-center border-0 bg-transparent text-[20px] font-medium"
      >
        {t`Contact us`}
      </button>

      {/* Contact Modal */}
      <ContactDialog openContact={openContact} setOpenContact={setOpenContact} />
    </>
  )
}
